import type { ReceiveReleasableLineDto, ReceiveReleaseContextDto, ReceiveReleaseRequest } from '@/entities/receive';
import type { BundleConfig } from '@/features/receive-confirm-arrival';

import type { ReleaseGoodsFormValues, ReleaseGoodsLineValues } from '../model/types';

const optionalText = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

export const isReleaseLineEmpty = (line: ReleaseGoodsLineValues): boolean => line.goodQty <= 0 && line.damagedQty <= 0;

/** What the shared unit calculator needs; no bundle configuration means inventory units only. */
export const toBundleConfig = (line: ReceiveReleasableLineDto): BundleConfig => ({
  bundleQtyAllowed: line.bundleQtyAllowed ?? false,
  ltusPerBundle: line.ltusPerBundle ?? 0,
  bundleTypeDescription: line.bundleTypeDescription,
  ltuTypeDescription: line.unit
});

/** Row identity — a line may come from several receipts. */
export const releaseLineKey = (line: Pick<ReceiveReleasableLineDto, 'receiveShipmentId' | 'lineId'>): string =>
  `${line.receiveShipmentId}|${line.lineId}`;

const toLineValues = ({ receiveShipmentId, lineId, goodQty, damagedQty }: ReceiveReleasableLineDto) => ({
  receiveShipmentId,
  lineId,
  goodQty,
  damagedQty
});

/**
 * Form defaults from the release context. As in legacy, every line starts with
 * everything releasable — a full release is one click — and the date starts at
 * the latest allowed day, which is today.
 */
export const buildReleaseDefaults = (
  context: ReceiveReleaseContextDto,
  maxReleaseDate: string
): ReleaseGoodsFormValues => ({
  releaseDate: maxReleaseDate,
  receptionDocumentNumber: context.receptionDocumentNumber ?? '',
  transporterCompanyReleasedTo: '',
  driverReleasedTo: '',
  receptionVerificationCode: 'D',
  remarks: '',
  printGoodsReleaseNote: false,
  lines: context.releasableLines.map(toLineValues)
});

/**
 * Entered quantities against a re-read context: what the operator typed stays
 * unless the stock shrank below it. A line the old form did not have starts full.
 */
export const clampToReleasable = (
  entered: ReleaseGoodsLineValues[],
  context: ReceiveReleaseContextDto
): ReleaseGoodsLineValues[] => {
  const previous = new Map(entered.map((line) => [releaseLineKey(line), line]));
  return context.releasableLines.map((line) => {
    const typed = previous.get(releaseLineKey(line));
    return typed
      ? {
          ...toLineValues(line),
          goodQty: Math.min(typed.goodQty, line.goodQty),
          damagedQty: Math.min(typed.damagedQty, line.damagedQty)
        }
      : toLineValues(line);
  });
};

/** Form values → request body. All-zero lines are left out, like on the receipt. */
export const buildReleaseBody = (
  values: ReleaseGoodsFormValues,
  context: ReceiveReleaseContextDto
): ReceiveReleaseRequest => ({
  receiveShipmentIds: context.receiveShipmentIds,
  releaseDate: values.releaseDate,
  receptionDocumentNumber: optionalText(values.receptionDocumentNumber),
  transporterCompanyReleasedTo: values.transporterCompanyReleasedTo.trim(),
  driverReleasedTo: values.driverReleasedTo.trim(),
  receptionVerificationCode: values.receptionVerificationCode,
  remarks: optionalText(values.remarks),
  lines: values.lines
    .filter((line) => !isReleaseLineEmpty(line))
    .map(({ receiveShipmentId, lineId, goodQty, damagedQty }) => ({ receiveShipmentId, lineId, goodQty, damagedQty })),
  printGoodsReleaseNote: values.printGoodsReleaseNote
});
